/**
 * 화자 구분 끝점 · 타임아웃 — 콘솔 쪽 판정 (2026-10-22).
 *
 * 서버 SSOT: `apps/chat-server/src/lib/external-api/diarize-adapters.ts`
 *   · `DIARIZE_PATH = '/v1/diarize'` · `resolveDiarizeUrl` — 경로가 비었으면 붙이고, 적혀 있으면 그대로 쓴다.
 *   · `timeout_sec` 10 ~ 3600 · 기본 1800.
 *
 * ## STT 와 다른 점 — 경로를 «막지 않는다»
 *
 * STT 끝점(`sttEndpoint.ts`)은 호스트까지만 받는다(경로를 적으면 저장을 잠근다). 화자 구분은 서버가
 * **경로가 적혀 있으면 그대로 쓴다**(`resolveDiarizeUrl`). 그래서 여기서는 경로를 오류로 보지 않고,
 * 화면이 「실제로는 무엇을 부르는지」만 보여 준다 — 서버와 다른 규칙을 콘솔이 따로 만들지 않는다.
 *
 * ## 한글 호스트를 바꿔 적지 않는다
 *
 * `new URL()` 의 `host` 는 한글 호스트를 퓨니코드(`xn--…`)로 바꾼다. 저장 값과 화면 값은
 * **적은 글자 그대로** 둔다(앞뒤 공백과 끝 슬래시 하나만 뗀다).
 */
import type { ExternalApiProvider } from '../../../api/externalApis';

/** 화자 구분 요청 경로 — 서버 `DIARIZE_PATH` 와 같은 글자. */
export const DIARIZE_PATH = '/v1/diarize';

export const DIARIZE_TIMEOUT_DEFAULT_SEC = 1800;
export const DIARIZE_TIMEOUT_MIN_SEC = 10;
export const DIARIZE_TIMEOUT_MAX_SEC = 3600;

/** 화자 구분이 쓰는 `config` 칸 — 서버와 같은 글자(`endpoint` · `timeout_sec`). 키는 여기 없다. */
export interface DiarizeConfig {
  endpoint: string;
  timeout_sec: number;
}

/** `config` 가 없거나 칸이 비어 있어도 빈 값으로 읽는다 — 타임아웃은 서버 기본값(1800)을 쓴다. */
export function readDiarizeConfig(p: Pick<ExternalApiProvider, 'config'>): DiarizeConfig {
  const c = (p.config ?? {}) as Partial<DiarizeConfig>;
  return {
    endpoint: typeof c.endpoint === 'string' ? c.endpoint : '',
    timeout_sec: typeof c.timeout_sec === 'number' ? c.timeout_sec : DIARIZE_TIMEOUT_DEFAULT_SEC,
  };
}

export type DiarizeEndpointProblem = 'empty' | 'no_scheme' | 'bad_scheme' | 'not_a_url';

export type DiarizeEndpointCheck =
  | { ok: true; value: string; callUrl: string; plaintext: boolean; hostOnly: boolean }
  | { ok: false; problem: DiarizeEndpointProblem; message: string };

const MESSAGE: Record<DiarizeEndpointProblem, string> = {
  empty: '끝점을 적으십시오.',
  no_scheme: 'http:// 또는 https:// 로 시작하는 주소를 적으십시오.',
  bad_scheme: 'http:// 또는 https:// 만 됩니다.',
  not_a_url: '주소를 읽을 수 없습니다 — 예: http://호스트:포트',
};

const fail = (problem: DiarizeEndpointProblem): DiarizeEndpointCheck => ({ ok: false, problem, message: MESSAGE[problem] });

/** 적은 글자 → 저장할 끝점 · 실제로 부를 주소 · 평문인가. */
export function checkDiarizeEndpoint(raw: string): DiarizeEndpointCheck {
  const text = raw.trim();
  if (text === '') return fail('empty');
  // 스킴 먼저 — 생략하면 `new URL('host:9294')` 가 오류 없이 엉뚱하게 읽힌다(sttEndpoint.ts 머리말과 같은 까닭).
  const origin = /^([a-zA-Z][a-zA-Z0-9+.-]*:\/\/[^/?#]*)/.exec(text)?.[1];
  if (!origin) return fail('no_scheme');
  let u: URL;
  try { u = new URL(text); } catch { return fail('not_a_url'); }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return fail('bad_scheme');
  if (u.hostname === '') return fail('not_a_url');
  const hostOnly = u.pathname === '' || u.pathname === '/';
  const value = hostOnly && u.search === '' && u.hash === '' ? origin : text;
  // 서버 `resolveDiarizeUrl` 과 같은 규칙 — 경로가 비었으면 붙이고, 적혀 있으면 그대로.
  const callUrl = hostOnly ? `${origin}${DIARIZE_PATH}${u.search}` : text;
  return { ok: true, value, callUrl, plaintext: u.protocol === 'http:', hostOnly };
}

/** 끝점 칸 아래 경고 한 줄 — `http://` 일 때만. 막지 않는다(저장은 된다). */
export const DIARIZE_PLAINTEXT_WARNING = '회의 녹음이 암호화 없이 지나갑니다. 사내망 안에서만 쓰십시오.';

export type DiarizeTimeoutCheck = { ok: true; value: number } | { ok: false; message: string };

/** 타임아웃 — 초. 비우면 기본값(1800)을 쓴다. */
export function checkDiarizeTimeout(raw: string): DiarizeTimeoutCheck {
  const text = raw.trim();
  if (text === '') return { ok: true, value: DIARIZE_TIMEOUT_DEFAULT_SEC };
  const n = Number(text);
  if (!Number.isInteger(n) || n < DIARIZE_TIMEOUT_MIN_SEC || n > DIARIZE_TIMEOUT_MAX_SEC) {
    return {
      ok: false,
      message: `타임아웃은 ${DIARIZE_TIMEOUT_MIN_SEC} ~ ${DIARIZE_TIMEOUT_MAX_SEC} 사이의 정수(초)로 적습니다.`,
    };
  }
  return { ok: true, value: n };
}

/**
 * 초 → 사람 단위(「30분」 · 「1시간 30분」 · 「45초」).
 * 표와 팝업이 같은 함수를 쓴다 — 같은 값이 두 곳에서 다른 모양이 되지 않게.
 */
export function formatDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return `${sec}초`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}시간`);
  if (m > 0) parts.push(`${m}분`);
  if (s > 0) parts.push(`${s}초`);
  return parts.join(' ');
}
